import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { useEffect, useRef } from 'react';
import { useNotification } from '../../Contexts/NotificationContext';
import usePendingState from '../../Hooks/usePendingState';

// Quill exports ordinary spaces as non-breaking spaces in semantic HTML.
const getContentHTML = (quill) => quill.getSemanticHTML().replace(/&nbsp;|\u00a0/g, ' ');

export const InputQuillEditor = ({ name, toolbar, value, onChange, disabled = false, onUploadStateChange }) => {
    const { setNotification } = useNotification();
    const editorRef = useRef(null);
    const quillInstance = useRef(null);
    const [uploading, setUploading, uploadingRef] = usePendingState();
    const uploadProps = useRef({ disabled, onUploadStateChange });
    uploadProps.current = { disabled, onUploadStateChange };

    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        if (editorRef.current && !quillInstance.current) {
            const imageHandler = async () => {
                if (uploadingRef.current || uploadProps.current.disabled) return;
                const input = document.createElement('input');
                input.setAttribute('type', 'file');
                input.setAttribute('accept', 'image/*');

                input.onchange = async () => {
                    const file = input.files[0];
                    if (!file || uploadingRef.current || uploadProps.current.disabled) return;
                    const range = quillInstance.current.getSelection(true);
                    setUploading(true);
                    uploadProps.current.onUploadStateChange?.(true);
                    const formData = new FormData();
                    formData.append('image', file);

                    try {
                        const response = await fetch(`${baseUrl}/manager/destaques/imagens/upload`, {
                            method: 'POST',
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                            },
                            body: formData,
                        });

                        if (!response.ok) {
                            throw new Error('Falha no upload da imagem.');
                        }

                        const data = await response.json();
                        const imageUrl = data.url;

                        quillInstance.current.insertEmbed(range.index, 'image', imageUrl);
                        quillInstance.current.setSelection(range.index + 1);
                    } catch (error) {
                        setNotification({
                            type: 'error',
                            message: error.message,
                            show: true,
                        });
                    } finally {
                        setUploading(false);
                        uploadProps.current.onUploadStateChange?.(false);
                    }
                };
                input.click();
            };

            const convertToolbarOptions = (toolbarItems) => {
                const toolbarConfig = [];

                toolbarItems.forEach((item) => {
                    // eslint-disable-next-line default-case
                    switch (item.toLowerCase()) {
                        case 'bold':
                            toolbarConfig.push('bold');
                            break;
                        case 'italic':
                            toolbarConfig.push('italic');
                            break;
                        case 'underline':
                            toolbarConfig.push('underline');
                            break;
                        case 'image':
                            toolbarConfig.push('image');
                            break;
                        case 'link':
                            toolbarConfig.push('link');
                            break;
                        case 'list':
                            toolbarConfig.push({ list: 'ordered' });
                            toolbarConfig.push({ list: 'bullet' });
                            break;
                        case 'align':
                            toolbarConfig.push({ align: [] });
                            break;
                    }
                });

                return toolbarConfig;
            };

            quillInstance.current = new Quill(editorRef.current, {
                theme: 'snow',
                placeholder: 'Digite seu texto aqui...',
                modules: {
                    toolbar: {
                        container: convertToolbarOptions(toolbar),
                        handlers: {
                            image: imageHandler,
                        },
                    },
                },
                formats: ['bold', 'italic', 'underline', 'strike', 'image', 'link', 'list', 'align'],
            });

        }
    }, [toolbar, accessToken, baseUrl, setNotification, setUploading, uploadingRef]);

    useEffect(() => {
        quillInstance.current?.enable(!disabled && !uploading);
    }, [disabled, uploading]);

    useEffect(() => {
        const quill = quillInstance.current;
        if (!quill) return;

        const handleTextChange = () => {
            const content = getContentHTML(quill);
            if (onChange) {
                onChange({
                    target: {
                        name,
                        value: content,
                    },
                });
            }
        };

        quill.on('text-change', handleTextChange);
        return () => quill.off('text-change', handleTextChange);
    }, [name, onChange]);

    useEffect(() => {
        const quill = quillInstance.current;
        if (quill && (value || '') !== getContentHTML(quill)) {
            quill.clipboard.dangerouslyPasteHTML(value || '', 'silent');
            // Normalize previously saved Quill markup even when the user only opens and saves.
            const content = getContentHTML(quill);
            if (onChange && content !== value) {
                onChange({ target: { name, value: content } });
            }
        }
    }, [value, name, onChange]);

    return (
        <div aria-busy={uploading}>
            <div ref={editorRef} />
            {uploading && <p role="status" className="mt-2 text-sm text-slate-600">Enviando imagem...</p>}
        </div>
    );
};
